import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ScrollReveal } from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Loader2,
  TrendingUp,
  Users,
  Eye,
  Target,
  Megaphone,
  BarChart3,
  Flame,
  Activity,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import { useSocialData, SocialChannel, SocialPost } from "@/hooks/useSocialData";

const CHANNEL_COLORS: Record<string, string> = {
  linkedin: "hsl(210 80% 50%)",
  facebook: "hsl(220 70% 55%)",
  instagram: "hsl(330 70% 55%)",
  x: "hsl(0 0% 70%)",
  tiktok: "hsl(170 80% 50%)",
  youtube: "hsl(0 80% 55%)",
  blog: "hsl(38 90% 55%)",
};

const CHANNEL_LABELS: Record<string, string> = {
  linkedin: "LinkedIn",
  facebook: "Facebook",
  instagram: "Instagram",
  x: "X (Twitter)",
  tiktok: "TikTok",
  youtube: "YouTube",
  blog: "Blog",
};

const campaignStatusColors: Record<string, string> = {
  active: "bg-emerald-500/15 text-emerald-400",
  completed: "bg-primary/15 text-primary",
  paused: "bg-orange-500/15 text-orange-400",
  draft: "bg-muted text-muted-foreground",
};

const campaignStatusLabels: Record<string, string> = {
  active: "Active",
  completed: "Terminée",
  paused: "En pause",
  draft: "Brouillon",
};

const SocialDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { channels: socialChannels, posts, loading, createPost, deletePost, updateChannel, refresh } = useSocialData();

  // Build channelKpis and campaigns from the new data format
  const channelKpis = socialChannels.map(ch => ({
    id: ch.id, name: ch.name, followers: ch.followers, engagement: ch.engagement, postsPerMonth: ch.posts_month, growth: ch.growth,
  }));
  const campaigns = posts.map(p => ({
    id: p.id, channel: p.channel, content: p.content, status: p.status,
    scheduledAt: p.scheduled_at, likes: p.likes, comments: p.comments, shares: p.shares,
  }));

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) return null;

  const totalFollowers = channelKpis.reduce((s, k) => s + (k.followers || 0), 0);
  const totalEngagement = channelKpis.reduce((s, k) => s + (k.engagement || 0), 0);
  const totalPosts = channelKpis.reduce((s, k) => s + (k.postsPerMonth || 0), 0);
  const activeCampaigns = campaigns.filter((c) => c.status === "active").length;

  const visitorsChartData = channelKpis.map((k) => ({
    channel: CHANNEL_LABELS[k.id] || k.name,
    visitors: k.followers,
    fill: CHANNEL_COLORS[k.id] || "hsl(38 90% 55%)",
  }));

  const radarData = channelKpis.map((k) => ({
    channel: CHANNEL_LABELS[k.id] || k.name,
    engagement: k.engagement,
    impact: k.postsPerMonth,
  }));

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <nav className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour
            </Button>
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Megaphone className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-semibold text-lg tracking-tight">Réseaux Sociaux</span>
          </div>
        </div>
      </nav>

      <main className="container py-8 space-y-8">
        {/* Header */}
        <ScrollReveal>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              Dashboard Réseaux Sociaux 📱
            </h1>
            <p className="text-muted-foreground mt-1">
              Performance de vos canaux de communication
            </p>
          </div>
        </ScrollReveal>

        {/* Overview KPIs */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-28 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Abonnés totaux", value: totalFollowers.toLocaleString("fr-FR"), icon: Users, color: "text-primary" },
              { label: "Engagement total", value: totalEngagement, icon: Activity, color: "text-orange-400" },
              { label: "Publications/mois", value: totalPosts, icon: Eye, color: "text-emerald-400" },
              { label: "Publications", value: campaigns.length, icon: Target, color: "text-primary" },
            ].map((kpi, i) => (
              <ScrollReveal key={kpi.label} delay={i * 80}>
                <Card className="hover:border-primary/30 transition-all duration-300">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">{kpi.label}</p>
                        <p className="text-2xl font-bold tabular-nums">{kpi.value}</p>
                      </div>
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        )}

        {/* Per-channel cards */}
        <ScrollReveal>
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Performance par canal
          </h2>
        </ScrollReveal>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-44 rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {channelKpis.map((kpi, i) => (
              <ScrollReveal key={kpi.id} delay={i * 60}>
                <Card className="hover:border-primary/30 transition-all duration-300">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <span
                        className="font-semibold"
                        style={{ color: CHANNEL_COLORS[kpi.id] }}
                      >
                        {CHANNEL_LABELS[kpi.id] || kpi.name}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        {kpi.growth || "0%"} croissance
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-muted-foreground text-xs">Abonnés</p>
                        <p className="font-semibold tabular-nums">{(kpi.followers || 0).toLocaleString("fr-FR")}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Engagement</p>
                        <p className="font-semibold tabular-nums">{kpi.engagement || 0}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Posts / mois</p>
                        <p className="font-semibold tabular-nums text-orange-400">{kpi.postsPerMonth || 0}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs">Croissance</p>
                        <p className="font-semibold tabular-nums">{kpi.growth || "0%"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        )}

        {/* Charts */}
        {!loading && (
          <div className="grid lg:grid-cols-2 gap-6">
            <ScrollReveal>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Eye className="h-5 w-5 text-primary" />
                    Visiteurs par canal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={visitorsChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 20% 20%)" />
                        <XAxis dataKey="channel" stroke="hsl(220 15% 55%)" fontSize={11} />
                        <YAxis stroke="hsl(220 15% 55%)" fontSize={12} tickFormatter={(v) => `${v / 1000}k`} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(222 28% 13%)",
                            border: "1px solid hsl(222 20% 20%)",
                            borderRadius: "8px",
                            color: "hsl(220 20% 92%)",
                          }}
                          formatter={(v: number) => [v.toLocaleString("fr-FR"), "Visiteurs"]}
                        />
                        <Bar dataKey="visitors" radius={[4, 4, 0, 0]}>
                          {visitorsChartData.map((entry, i) => (
                            <rect key={i} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={100}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="h-5 w-5 text-primary" />
                    Engagement & Impact
                  </CardTitle>
                  <CardDescription>Comparaison des canaux</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="hsl(222 20% 20%)" />
                        <PolarAngleAxis dataKey="channel" stroke="hsl(220 15% 55%)" fontSize={11} />
                        <PolarRadiusAxis stroke="hsl(220 15% 55%)" fontSize={10} />
                        <Radar name="Engagement %" dataKey="engagement" stroke="hsl(38 90% 55%)" fill="hsl(38 90% 55%)" fillOpacity={0.2} />
                        <Radar name="Impact /10" dataKey="impact" stroke="hsl(150 60% 45%)" fill="hsl(150 60% 45%)" fillOpacity={0.15} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "hsl(222 28% 13%)",
                            border: "1px solid hsl(222 20% 20%)",
                            borderRadius: "8px",
                            color: "hsl(220 20% 92%)",
                          }}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </ScrollReveal>
          </div>
        )}

        {/* Campaigns */}
        <ScrollReveal>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Campagnes
                </CardTitle>
                <Badge variant="secondary">{campaigns.length} campagnes</Badge>
              </div>
              <CardDescription>Suivi et gestion des campagnes sur tous vos canaux</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
                </div>
              ) : (
                <div className="space-y-3">
                  {campaigns.map((c) => (
                    <div
                      key={c.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg bg-secondary/30 border border-border/50 hover:border-primary/20 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${campaignStatusColors[c.status] || campaignStatusColors["draft"]}`}
                        >
                          {campaignStatusLabels[c.status] || c.status}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{c.content || "Publication"}</p>
                          <p className="text-xs text-muted-foreground" style={{ color: CHANNEL_COLORS[c.channel] }}>
                            {CHANNEL_LABELS[c.channel] || c.channel}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground shrink-0">
                        {c.likes > 0 && <span>{c.likes} likes</span>}
                        {c.comments > 0 && <span>{c.comments} commentaires</span>}
                        {c.shares > 0 && <span className="text-emerald-400">{c.shares} partages</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </ScrollReveal>
      </main>
    </div>
  );
};

export default SocialDashboard;
